<script setup lang="ts">
/**
 * /approvals/:id — the deep link MCP messages point to. Shows who wants to
 * change what, the dry-run plan, and lets the requester (or an approver)
 * approve (typing the target for high risk) or decline; then follows the job.
 * Four-eyes changes: the requester confirms, a second person approves (or the
 * requester approves alone with a break-glass reason); the server decides
 * who may do what (`viewer`).
 */
import { Check, ChevronRight, Clock, RotateCcw, ShieldAlert, Siren, Users, X } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { isProblem } from "@/api/client";
import { approveOperation, confirmationText, declineOperation, undoOperation, useOperation } from "@/api/operations";
import AiReview from "@/components/approvals/AiReview.vue";
import PlanView from "@/components/approvals/PlanView.vue";
import DescriptionList, { type DescriptionItem } from "@/components/common/DescriptionList.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FormError from "@/components/common/FormError.vue";
import JsonView from "@/components/common/JsonView.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import ResultView from "@/components/common/ResultView.vue";
import RiskBadge from "@/components/common/RiskBadge.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import JobProgress from "@/components/jobs/JobProgress.vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { confirm } from "@/composables/useConfirm";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { formatDateTime } from "@/lib/format";

definePage({ meta: { title: "Approval" } });

const route = useRoute("/approvals/[id]");
const router = useRouter();
const id = computed(() => route.params.id);
const queryClient = useQueryClient();
const { me } = useSession();

const op = useOperation(id);
const data = computed(() => op.data.value);
const typed = ref("");
const busy = ref(false);
const actionError = ref<unknown>(null);
const followJob = ref<string | null>(null);

const pending = computed(() => data.value?.status === "awaiting_approval");
const expired = computed(() => Boolean(pending.value && data.value?.expiresAt && new Date(data.value.expiresAt).getTime() < Date.now()));
const highRisk = computed(() => data.value?.risk === "high");
const expected = computed(() => (data.value ? confirmationText(data.value) : ""));
const isRequester = computed(() => data.value?.viewer?.isRequester ?? data.value?.principalId === me.value?.id);
const fourEyes = computed(() => Boolean(data.value?.requiresSecondApprover));
const typedOk = computed(() => !highRisk.value || typed.value === expected.value);
const mayApprove = computed(() => pending.value && !expired.value && Boolean(data.value?.viewer?.canApprove));
const canApprove = computed(() => mayApprove.value && typedOk.value);
const breakGlassOpen = ref(false);
const breakGlassReason = ref("");
const BREAKGLASS_MIN = 15;
const canBreakGlass = computed(() => pending.value && !expired.value && Boolean(data.value?.viewer?.canBreakGlass));
const breakGlassReady = computed(() => canBreakGlass.value && typedOk.value && breakGlassReason.value.trim().length >= BREAKGLASS_MIN);
const approveLabel = computed(() => (!fourEyes.value ? t.approvals.approve : isRequester.value ? t.approvals.confirmOwn : t.approvals.approveAsSecond));
/** What the decision panel says, for the current viewer and state. */
const decisionText = computed(() => {
  const d = data.value;
  if (!d) return "";
  const who = d.requester ?? "Someone";
  if (!fourEyes.value) {
    if (isRequester.value) return "You asked for this change. Review the plan, then approve to run it.";
    // Held because the chat could not ask the requester: an approver's approval waits for their confirmation.
    if (d.approver && !d.requesterConfirmedAt && d.status === "awaiting_approval") return t.approvals.waitingForRequester(who);
    return `${who} asked for this change and needs your approval.`;
  }
  if (isRequester.value) return d.requesterConfirmedAt ? t.approvals.waitingForSecond : "You asked for this change. Review the plan and confirm it; then a second person must approve it.";
  if (d.approver && !d.requesterConfirmedAt) return t.approvals.waitingForRequester(who);
  return `${who} asked for this change. It needs a second person: review the plan and approve it if it is right.`;
});
/** The facts under the title; the values that are links or times render through slots named after `key`. */
const facts = computed<DescriptionItem[]>(() => {
  const d = data.value;
  if (!d) return [];
  return [
    { label: t.approvals.requester, value: d.requester ?? "unknown" },
    { label: t.approvals.tool, value: d.tool, mono: true },
    ...(d.target ? [{ label: t.approvals.target, value: d.target }] : []),
    ...(d.datasource ? [{ label: "Datasource", value: d.datasource, mono: true }] : []),
    { label: "Requested", key: "requested" },
    ...(d.undoOf ? [{ label: t.approvals.undoOf, key: "undoOf" }] : []),
    ...(fourEyes.value && d.requesterConfirmedAt ? [{ label: t.approvals.confirmedByRequester, key: "confirmedAt" }] : []),
    ...(d.approver ? [{ label: t.approvals.approvedBy, key: "approver" }] : []),
  ];
});
const canUndo = computed(() => data.value?.status === "executed" && data.value.reversible && isRequester.value);
const jobId = computed(() => followJob.value ?? (data.value && ["executing", "undoing", "executed", "failed", "undone"].includes(data.value.status) ? data.value.jobId : null));

async function refresh() {
  await Promise.all([op.refetch(), queryClient.invalidateQueries({ queryKey: ["operations"] }), queryClient.invalidateQueries({ queryKey: ["jobs"] })]);
}

async function approve() {
  if (!data.value || !canApprove.value) return;
  busy.value = true;
  actionError.value = null;
  try {
    const res = await approveOperation(data.value.id, highRisk.value ? typed.value : undefined);
    if (res.status === "approved") followJob.value = res.jobId;
    toast.success(res.status === "awaiting_second_approver" ? t.approvals.confirmedOwn : res.status === "awaiting_requester" ? t.approvals.approvedAsSecond : t.approvals.approved);
    await refresh();
  } catch (err) {
    actionError.value = err;
    if (isProblem(err) && err.status === 410) await refresh();
  } finally {
    busy.value = false;
  }
}

async function approveBreakGlass() {
  if (!data.value || !breakGlassReady.value) return;
  busy.value = true;
  actionError.value = null;
  try {
    const res = await approveOperation(data.value.id, highRisk.value ? typed.value : undefined, breakGlassReason.value.trim());
    followJob.value = res.jobId;
    toast.success(t.approvals.breakGlassDone);
    await refresh();
  } catch (err) {
    actionError.value = err;
  } finally {
    busy.value = false;
  }
}

function scrollToDecision() {
  const el = document.getElementById("decision");
  el?.scrollIntoView({ behavior: "smooth", block: "start" });
  el?.querySelector("input")?.focus({ preventScroll: true });
}

async function decline() {
  if (!data.value) return;
  const answer = await confirm({ title: t.approvals.decline, description: t.approvals.declineConfirm, confirmLabel: t.approvals.decline, destructive: true });
  if (!answer) return;
  busy.value = true;
  actionError.value = null;
  try {
    await declineOperation(data.value.id);
    toast.success(t.approvals.declined);
    await refresh();
  } catch (err) {
    actionError.value = err;
  } finally {
    busy.value = false;
  }
}

async function undo() {
  if (!data.value) return;
  // Undo needs what the change needed: high risk ⇒ type the target; four-eyes ⇒ a second person approves the undo.
  const typed = highRisk.value ? expected.value : undefined;
  const answer = await confirm({ title: t.changes.undo, description: t.changes.undoConfirm, confirmLabel: t.changes.undo, ...(typed ? { typeToConfirm: typed } : {}) });
  if (!answer) return;
  busy.value = true;
  actionError.value = null;
  try {
    const res = await undoOperation(data.value.id, typed);
    if (res.status === "awaiting_second_approver" && res.operationId) {
      toast.success(t.changes.undoAwaitingSecond);
      await router.push(`/approvals/${res.operationId}`);
      return;
    }
    followJob.value = res.jobId;
    toast.success(t.changes.undoStarted);
    await refresh();
  } catch (err) {
    actionError.value = err;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div>
    <ListSkeleton v-if="op.isPending.value" :rows="5" />
    <ErrorState v-else-if="op.error.value" :error="op.error.value" what="Change" @retry="op.refetch()" />
    <template v-else-if="data">
      <PageHeader
        :title="data.plan.summary || data.tool"
        :crumbs="[{ label: t.nav.approvals, to: '/approvals' }, { label: data.tool }]"
      >
        <template #badges>
          <StatusBadge :status="expired ? 'expired' : data.status" />
          <RiskBadge :risk="data.risk" />
          <Badge v-if="fourEyes" variant="info"><Users /> {{ t.approvals.fourEyes }}</Badge>
          <Badge v-if="data.destructive" variant="danger"><ShieldAlert /> {{ t.approvals.destructive }}</Badge>
        </template>
        <template #meta>
          <DescriptionList :items="facts" layout="inline" dense class="mt-3">
            <template #requested><RelativeTime :value="data.createdAt" /></template>
            <template #undoOf>
              <RouterLink v-if="data.undoOf" :to="`/approvals/${data.undoOf}`" class="font-mono underline underline-offset-2">{{ data.undoOf.slice(0, 8) }}</RouterLink>
            </template>
            <template #confirmedAt><RelativeTime v-if="data.requesterConfirmedAt" :value="data.requesterConfirmedAt" /></template>
            <template #approver>
              <span class="font-medium">{{ data.approver }}</span><span v-if="data.approvedAt" class="text-muted-foreground"> · <RelativeTime :value="data.approvedAt" /></span>
            </template>
          </DescriptionList>
        </template>
      </PageHeader>

      <!-- grid-cols-1 (a minmax(0,1fr) track): an implicit auto column would grow to the widest unbreakable line -->
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div class="flex min-w-0 flex-col gap-6">
          <AiReview v-if="data.aiReview" :review="data.aiReview" />
          <PlanView :plan="data.plan" />
          <section v-if="data.result !== null && data.result !== undefined" class="flex flex-col gap-2">
            <h3 class="section-title">{{ t.jobs.result }}</h3>
            <ResultView :value="data.result" />
          </section>
          <p v-if="data.error" role="alert" class="rounded-lg border border-destructive/30 bg-destructive-soft p-3 text-sm [overflow-wrap:anywhere] text-destructive">{{ data.error }}</p>
          <p v-if="data.breakglassReason" class="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive-soft/50 p-3 text-sm [overflow-wrap:anywhere]">
            <Siren class="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
            <span><span class="font-medium">{{ t.approvals.breakGlassUsed }}:</span> {{ data.breakglassReason }}</span>
          </p>
        </div>

        <!-- Decision panel: sticky on desktop, at the bottom of the flow on phones -->
        <aside class="flex min-w-0 flex-col gap-4 lg:sticky lg:top-20 lg:self-start">
          <div v-if="pending" id="decision" class="surface flex scroll-mt-20 flex-col gap-4 p-4">
            <template v-if="expired">
              <p class="flex items-start gap-2 text-sm"><Clock class="mt-0.5 size-4 shrink-0 text-muted-foreground" /> {{ t.approvals.expired }}</p>
            </template>
            <template v-else>
              <p class="text-sm text-muted-foreground">{{ decisionText }}</p>
              <p v-if="fourEyes" class="flex items-start gap-2 rounded-md bg-info-soft px-3 py-2 text-xs"><Users class="mt-0.5 size-3.5 shrink-0" aria-hidden="true" /> {{ data.fourEyesReason ? `${t.approvals.fourEyesNote} (${data.fourEyesReason})` : t.approvals.fourEyesNote }}</p>
              <p v-if="fourEyes && !isRequester && mayApprove && !data.requesterConfirmedAt" class="text-xs text-muted-foreground">{{ t.approvals.requesterPending(data.requester ?? "The requester") }}</p>
              <p v-if="data.expiresAt" class="text-xs text-muted-foreground">{{ t.approvals.expires }} {{ formatDateTime(data.expiresAt) }}</p>
              <div v-if="highRisk && (mayApprove || canBreakGlass)" class="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive-soft/50 p-3">
                <p class="flex items-start gap-2 text-sm"><ShieldAlert class="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" /> {{ t.approvals.highRiskNote }}</p>
                <Label for="typed" class="text-sm">Type <code class="rounded bg-background px-1 font-semibold">{{ expected }}</code></Label>
                <Input id="typed" v-model="typed" autocomplete="off" spellcheck="false" :aria-invalid="typed.length > 0 && typed !== expected" />
              </div>
              <FormError :error="actionError" />
              <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                <Button v-if="mayApprove" size="lg" :disabled="!canApprove || busy" @click="approve"><Check /> {{ approveLabel }}</Button>
                <Button v-if="data.viewer.canDecline" size="lg" variant="outline" :disabled="busy" @click="decline"><X /> {{ t.approvals.decline }}</Button>
              </div>
              <div v-if="canBreakGlass" class="flex flex-col gap-2 border-t pt-3">
                <Button v-if="!breakGlassOpen" variant="ghost" size="sm" class="self-start" @click="breakGlassOpen = true"><Siren /> {{ t.approvals.breakGlass }}</Button>
                <template v-else>
                  <p class="text-xs text-muted-foreground">{{ t.approvals.breakGlassHint }}</p>
                  <Label for="bg-reason" class="text-sm">{{ t.approvals.breakGlassReason }}</Label>
                  <Textarea id="bg-reason" v-model="breakGlassReason" rows="3" :aria-invalid="breakGlassReason.length > 0 && breakGlassReason.trim().length < BREAKGLASS_MIN" />
                  <Button variant="destructive" :disabled="!breakGlassReady || busy" @click="approveBreakGlass"><Siren /> {{ t.approvals.breakGlass }}</Button>
                </template>
              </div>
            </template>
          </div>

          <div v-else-if="canUndo" class="surface flex flex-col gap-3 p-4">
            <p class="text-sm text-muted-foreground">This change can be undone: the saved before-state is restored.</p>
            <FormError :error="actionError" />
            <Button variant="outline" :disabled="busy" @click="undo"><RotateCcw /> {{ t.changes.undo }}</Button>
          </div>

          <JobProgress v-if="jobId" :job-id="jobId" @finished="refresh" />

          <details class="group surface overflow-hidden text-sm">
            <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 font-medium transition-colors hover:bg-state-hover [&::-webkit-details-marker]:hidden">
              <ChevronRight class="size-4 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
              Technical details
            </summary>
            <div class="border-t p-3">
              <JsonView :value="{ id: data.id, capability: data.capability, confirmedVia: data.confirmedVia, jobId: data.jobId, requiresSecondApprover: data.requiresSecondApprover, aiVerdict: data.aiReview?.verdict ?? null }" />
            </div>
          </details>
        </aside>
      </div>

      <!-- Phones: the decision stays in reach while reading the plan -->
      <div v-if="pending && !expired && mayApprove" class="h-20 lg:hidden" aria-hidden="true" />
      <div v-if="pending && !expired && mayApprove" class="fixed inset-x-0 bottom-0 z-20 flex gap-2 border-t bg-background/95 p-3 backdrop-blur lg:hidden">
        <Button variant="outline" class="flex-1" :disabled="busy" @click="decline"><X /> {{ t.approvals.decline }}</Button>
        <Button class="flex-1" :disabled="busy || (!highRisk && !canApprove)" @click="highRisk && !canApprove ? scrollToDecision() : approve()">
          <Check /> {{ highRisk && !canApprove ? "Review & approve" : approveLabel }}
        </Button>
      </div>
    </template>
  </div>
</template>
